import { useTranslation } from 'react-i18next';
import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { showLockedScreen } from '../../slices/inactivityScreenSlice';
import { useLoginActivityMutation } from '../../../../features/activity/api/activityApi';
import { LOGIN_ACTIVITY_KEY } from '../../../../constants';
import {
    SubdomainStatus,
    setAuthError,
    setLoggedInStatus,
    setLoginActivityComplete,
    setSubdomainValid,
} from '../../slices/authSlice';
import { useGetTenantBySubdomainQuery } from '../../../../features/tenant/tenantApi';
import { useNavigate } from 'react-router-dom';
import { getSubdomain } from '../../../../features/utils';

export enum LoginErrors {
    BLOCKED_USER = 'user is blocked',
    INACTIVE_TENANT = 'Tenant must have active status to get access to resources',
    CONNECTION_IS_NOT_ENABLED = 'the connection is not enabled',
    ORGANIZATION_CONNECTION_ERROR = 'client requires organization membership, but user does not belong to any organization',
    MISSING_SUBDOMAIN = 'Missing subdomain',
    FETCH_ERROR = 'Error fetching tenant data',
    INVITATION_ERROR = 'invitation not found or already used',
}

export const Auth0RedirectHandler: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { loginWithRedirect, isAuthenticated, isLoading, error } = useAuth0();
    const [loginActivity] = useLoginActivityMutation();
    const isLoginActivitySent = localStorage.getItem(LOGIN_ACTIVITY_KEY) === 'true';

    const handleAuthError = (message: string, canBackToLogin: boolean) => {
        dispatch(
            showLockedScreen({
                inactivityReason: message,
                showLoginButton: canBackToLogin,
            })
        );
    };
    const navigate = useNavigate();
    const subdomain = getSubdomain();

    const {
        data: tenant,
        error: tenantError,
        isLoading: isTenantLoading,
    } = useGetTenantBySubdomainQuery(subdomain!, { skip: !subdomain || subdomain === 'fd' });

    useEffect(() => {
        dispatch(setAuthError(true));
    }, [error]);

    useEffect(() => {
        if (tenantError) {
            dispatch(setSubdomainValid(SubdomainStatus.INVALID));
            return;
        }

        if (!isTenantLoading) {
            dispatch(setSubdomainValid(SubdomainStatus.VALID));

            if (isLoginActivitySent) {
                dispatch(setLoggedInStatus(true));
            }

            if (!isLoading && !isAuthenticated) {
                const searchParams = new URLSearchParams(window.location.search);
                const invitation = searchParams.get('invitation');
                const organization = searchParams.get('organization');
                const organization_name = searchParams.get('organization_name');
                if (organization_name) {
                    localStorage.setItem('loginRedirect', organization_name);
                }
                if (invitation && organization && organization_name) {
                    loginWithRedirect({
                        authorizationParams: {
                            invitation: invitation,
                            organization: organization,
                            redirect_uri: window.location.origin,
                            screen_hint: 'signup',
                        },
                    });
                } else if (error?.message === LoginErrors.BLOCKED_USER) {
                    handleAuthError(t('account-is-locked'), true);
                } else if (error?.message === LoginErrors.CONNECTION_IS_NOT_ENABLED) {
                    handleAuthError(t('connection-is-not-enabled'), false);
                } else if (error?.message === LoginErrors.ORGANIZATION_CONNECTION_ERROR) {
                    handleAuthError(t('wrong-authentication-method'), false);
                } else if (error?.message === LoginErrors.INVITATION_ERROR) {
                    handleAuthError(t('invitation-error'), true);
                } else if (error) {
                    console.log(error.message);
                    handleAuthError(t('common-error'), false);
                } else {
                    localStorage.removeItem(LOGIN_ACTIVITY_KEY);
                    loginWithRedirect({
                        authorizationParams: {
                            organization: tenant?.organizationId,
                        },
                    });
                }
            } else if (isAuthenticated && !isLoginActivitySent) {
                localStorage.setItem(LOGIN_ACTIVITY_KEY, 'true');
                dispatch(setLoggedInStatus(true));
                loginActivity().then((e) => {
                    dispatch(setLoginActivityComplete(true));
                    if ((e as { [key: string]: any })?.error?.data === LoginErrors.INACTIVE_TENANT) {
                        handleAuthError(t('tenant-is-inactive'), true);
                    }
                });
            }
        }
    }, [
        loginWithRedirect,
        isAuthenticated,
        isLoading,
        dispatch,
        error,
        t,
        isLoginActivitySent,
        loginActivity,
        subdomain,
        tenant,
        tenantError,
        isTenantLoading,
        navigate,
    ]);

    return null;
};

export default Auth0RedirectHandler;
